import { useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription, Button, Card, View, Text, InputField, Tooltip, Icon, DropdownField, EmptyState, Spinner } from "../components/base";
import { Tabs } from "../components/base/Tabs";
import { PageLayout } from "../components/PageLayout";
import { FilterSection, PillGroup, SortHeader, SortState } from "../components/lab/LabControls";
import { TIME_RANGE_OPTIONS, TimeRange, formatCount, formatMs, getDateRange } from "../components/lab/labFormat";
import { useAppDispatch, useAppSelector } from "../redux";
import { clear as clearTtsLab, getAggregates as getTtsAggregates } from "../redux/reducers/ttsLabSlice";
import { ProviderLogo } from "../components/ProviderLogo";
import { makeRequest } from "../utils/request";

// Provider configuration for the explorer
interface ProviderConfig {
  label: string;
  idLabel: string;
  idPlaceholder: string;
  idHelp: string;
  exampleVoiceId: string;
  exampleVoiceName: string;
  voiceLibraryUrl: string;
}

const PROVIDER_CONFIG: Record<string, ProviderConfig> = {
  openai: {
    label: "OpenAI",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., alloy, echo, shimmer, nova, fable, onyx, sage, ash, coral",
    idHelp: "OpenAI uses voice names as identifiers",
    exampleVoiceId: "alloy",
    exampleVoiceName: "Alloy",
    voiceLibraryUrl: "https://platform.openai.com/docs/guides/text-to-speech#voice-options",
  },
  cartesia: {
    label: "Cartesia",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., a0e99841-438c-4a64-b679-ae501e7d6091",
    idHelp: "UUID from Cartesia's voice library",
    exampleVoiceId: "a0e99841-438c-4a64-b679-ae501e7d6091",
    exampleVoiceName: "Barbershop Man",
    voiceLibraryUrl: "https://play.cartesia.ai/voices",
  },
  azure: {
    label: "Azure",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., en-US-JennyNeural, zh-CN-XiaoxiaoNeural",
    idHelp: "Full Azure voice name including locale",
    exampleVoiceId: "en-US-JennyNeural",
    exampleVoiceName: "Jenny (en-US)",
    voiceLibraryUrl: "https://speech.microsoft.com/portal/voicegallery",
  },
  google: {
    label: "Google",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., en-US-Wavenet-D, en-GB-Neural2-A",
    idHelp: "Google Cloud TTS voice name",
    exampleVoiceId: "en-US-Wavenet-D",
    exampleVoiceName: "Wavenet D (en-US)",
    voiceLibraryUrl: "https://docs.cloud.google.com/text-to-speech/docs/list-voices-and-types#chirp3_hd_voices",
  },
  minimax: {
    label: "Minimax",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., male-qn-qingse, female-shaonv",
    idHelp: "Minimax voice identifier",
    exampleVoiceId: "male-qn-qingse",
    exampleVoiceName: "Qingse (Male)",
    voiceLibraryUrl: "https://www.minimax.io/audio/voices",
  },
  fish_audio: {
    label: "Fish Audio",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., d13f84b987ad4f22b56d2b47f4eb838e",
    idHelp: "Fish Audio reference ID",
    exampleVoiceId: "d13f84b987ad4f22b56d2b47f4eb838e",
    exampleVoiceName: "AD (Male)",
    voiceLibraryUrl: "https://fish.audio/app/discovery/",
  },
  inworld: {
    label: "Inworld",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., en_us_male_1",
    idHelp: "Inworld voice identifier",
    exampleVoiceId: "Alex",
    exampleVoiceName: "Alex",
    voiceLibraryUrl: "https://platform.inworld.ai/",
  },
  gradium: {
    label: "Gradium",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., YTpq7expH9539ERJ, LFZvm12tW_z0xfGo",
    idHelp: "Gradium voice identifier",
    exampleVoiceId: "YTpq7expH9539ERJ",
    exampleVoiceName: "Emma (en-US)",
    voiceLibraryUrl: "https://us.api.gradium.ai/studio/voices/library",
  },
  qwen3: {
    label: "Qwen3",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., Cherry, Serena, Ethan, Chelsie, Ryan, Aiden",
    idHelp: "Qwen3 TTS voice name (case-sensitive)",
    exampleVoiceId: "Cherry",
    exampleVoiceName: "Cherry",
    voiceLibraryUrl: "https://www.alibabacloud.com/help/en/model-studio/qwen-tts-realtime",
  },
  elevenlabs: {
    label: "ElevenLabs",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., 21m00Tcm4TlvDq8ikWAM",
    idHelp: "ElevenLabs voice ID from the voice library",
    exampleVoiceId: "21m00Tcm4TlvDq8ikWAM",
    exampleVoiceName: "Rachel",
    voiceLibraryUrl: "https://elevenlabs.io/app/voice-library",
  },
  xai: {
    label: "xAI Grok",
    idLabel: "Voice",
    idPlaceholder: "e.g., eve",
    idHelp: "One of: ara, eve, leo, rex, sal",
    exampleVoiceId: "eve",
    exampleVoiceName: "Eve",
    voiceLibraryUrl: "https://docs.x.ai/docs/models/grok-tts",
  },
};

// ─── Recommended Voices ─────────────────────────────────────────────────────────

interface RecommendedVoice {
  name: string;
  provider: string;
  voiceId: string;
  description: string;
}

interface UseCase {
  label: string;
  icon: string;
  voices: RecommendedVoice[];
}

const RECOMMENDED_VOICES: UseCase[] = [
  {
    label: "Restaurants",
    icon: "recipe",
    voices: [
      { name: "ash", provider: "openai", voiceId: "ash", description: "Male · Warm, friendly presenter — great for welcoming callers" },
      {
        name: "Welcome Lady",
        provider: "cartesia",
        voiceId: "f80e7298-93f5-46d0-86f2-b8f29cfc88bd",
        description: "Female · Welcoming, friendly voice built for hospitality",
      },
      { name: "Jason", provider: "azure", voiceId: "en-US-JasonNeural", description: "Male · Natural, approachable voice for handling reservations" },
      { name: "Matilda", provider: "elevenlabs", voiceId: "XrExE9yKIg1WjnnlVkGX", description: "Female · Warm voice ideal for friendly interactions" },
    ],
  },
  {
    label: "Insurance",
    icon: "shield",
    voices: [
      { name: "onyx", provider: "openai", voiceId: "onyx", description: "Male · Deep, authoritative voice that conveys professional credibility" },
      {
        name: "Luke",
        provider: "cartesia",
        voiceId: "7b2c0a2e-3dd3-4a44-b16b-26ecd8134279",
        description: "Male · Authoritative, commanding voice for persuasive conversations",
      },
      { name: "Aria", provider: "azure", voiceId: "en-US-AriaNeural", description: "Female · Confident, professional voice with authentic warmth" },
      {
        name: "Serena",
        provider: "elevenlabs",
        voiceId: "pMsXgVXv3BLzUgSXRplE",
        description: "Female · Pleasant, professional voice for consultative conversations",
      },
    ],
  },
  {
    label: "Healthcare",
    icon: "activity",
    voices: [
      { name: "sage", provider: "openai", voiceId: "sage", description: "Neutral · Calm, wise voice that puts patients at ease" },
      {
        name: "Sophie",
        provider: "cartesia",
        voiceId: "bf0a246a-8642-498a-9950-80c35e9276b5",
        description: "Female · Gentle, soothing voice ideal for sensitive conversations",
      },
      { name: "Sara", provider: "azure", voiceId: "en-US-SaraNeural", description: "Female · Gentle, soft-spoken voice with a reassuring tone" },
      { name: "Thomas", provider: "elevenlabs", voiceId: "GBv7mTt0atIp3Br8iCZE", description: "Male · Calm, composed voice ideal for patient interactions" },
    ],
  },
  {
    label: "Support",
    icon: "support_agent",
    voices: [
      { name: "alloy", provider: "openai", voiceId: "alloy", description: "Neutral · Balanced, patient voice suited for support workflows" },
      {
        name: "David",
        provider: "cartesia",
        voiceId: "da69d796-4603-4419-8a95-293bfc5679eb",
        description: "Male · Warm, friendly voice that builds customer rapport",
      },
      { name: "Guy", provider: "azure", voiceId: "en-US-GuyNeural", description: "Male · Friendly, conversational, light-hearted voice" },
      { name: "Sarah", provider: "elevenlabs", voiceId: "EXAVITQu4vr4xnSDxMaL", description: "Female · Soft, gentle voice for empathetic support" },
    ],
  },
  {
    label: "Real Estate",
    icon: "home",
    voices: [
      { name: "coral", provider: "openai", voiceId: "coral", description: "Female · Vibrant, warm voice that generates excitement about listings" },
      {
        name: "Brooke",
        provider: "cartesia",
        voiceId: "6f84f4b8-58a2-430c-8c79-688dad597532",
        description: "Female · Professional, clear voice for business conversations",
      },
      { name: "Davis", provider: "azure", voiceId: "en-US-DavisNeural", description: "Male · Engaging, charismatic voice that conveys confidence" },
      { name: "Brian", provider: "elevenlabs", voiceId: "nPczCjzI2devNBz1zQrb", description: "Male · Deep, rich voice with polished delivery" },
    ],
  },
  {
    label: "Banking",
    icon: "payments",
    voices: [
      { name: "echo", provider: "openai", voiceId: "echo", description: "Male · Deep, resonant voice that conveys measured authority" },
      { name: "Jenny", provider: "azure", voiceId: "en-US-JennyNeural", description: "Female · Warm, sincere voice with natural conversational pace" },
      { name: "Alnilam", provider: "google", voiceId: "en-US-Chirp3-HD-Alnilam", description: "Male · Central, balanced voice with professional clarity" },
      { name: "Rachel", provider: "elevenlabs", voiceId: "21m00Tcm4TlvDq8ikWAM", description: "Female · Calm voice with composed, trustworthy delivery" },
    ],
  },
  {
    label: "Retail",
    icon: "storefront",
    voices: [
      { name: "shimmer", provider: "openai", voiceId: "shimmer", description: "Female · Bright, cheerful voice that creates an inviting experience" },
      { name: "Algieba", provider: "google", voiceId: "en-US-Chirp3-HD-Algieba", description: "Male · Bright, energetic voice great for promotions" },
      { name: "Jenny", provider: "azure", voiceId: "en-US-JennyNeural", description: "Female · Warm, pleasant voice with cheerful delivery" },
      { name: "Chris", provider: "elevenlabs", voiceId: "iP95p4xoKVk53GoZ742B", description: "Male · Casual, relatable voice for conversational shopping" },
    ],
  },
  {
    label: "Travel",
    icon: "flight",
    voices: [
      { name: "nova", provider: "openai", voiceId: "nova", description: "Female · Clear, conversational voice for itineraries and bookings" },
      { name: "Puck", provider: "google", voiceId: "en-US-Chirp3-HD-Puck", description: "Male · Lively, expressive voice with natural warmth" },
      { name: "Aria", provider: "azure", voiceId: "en-US-AriaNeural", description: "Female · Confident, clear voice with authentic warmth" },
      { name: "Antoni", provider: "elevenlabs", voiceId: "ErXwobaYiN019PkySvjV", description: "Male · Well-rounded voice for versatile interactions" },
    ],
  },
];

export const VoiceLabPage = () => {
  const [activeTab, setActiveTab] = useState<string>("explorer");

  return (
    <PageLayout
      title="TTS Lab"
      description="Test TTS providers directly with custom voice IDs, compare voices, and measure time-to-first-audio."
    >
      <Tabs
        className="mt-4"
        tabs={[
          { id: "explorer", label: "Explorer", icon: "voice-over" },
          { id: "latency", label: "Benchmarks", icon: "bar-chart-2" },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />
      {activeTab === "explorer" && <ProviderExplorerTab />}
      {activeTab === "latency" && <ProviderLatencyTab />}
    </PageLayout>
  );
};

// ─── Provider Explorer ──────────────────────────────────────────────────────────

interface ProviderRow {
  id: string;
  provider: string;
  voiceId: string;
  displayName?: string;
  status: "idle" | "generating" | "done" | "error";
  audioUrl?: string;
  audioBlob?: Blob;
  ttfaMs?: number;
  error?: string;
}

let rowIdCounter = 0;
const nextRowId = () => `row-${++rowIdCounter}`;

const PROVIDER_OPTIONS = Object.entries(PROVIDER_CONFIG).map(([key, config]) => ({
  label: config.label,
  value: key,
}));

const ProviderExplorerTab = () => {
  const [previewText, setPreviewText] = useState(
    "Hello, this is a preview of my voice. I can help you with various tasks and speak in a natural, friendly tone.",
  );
  const [rows, setRows] = useState<ProviderRow[]>([{ id: nextRowId(), provider: "openai", voiceId: "", status: "idle" }]);
  const [isRecommendedOpen, setIsRecommendedOpen] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState(0);

  const addRow = () => {
    setRows((prev) => [...prev, { id: nextRowId(), provider: "openai", voiceId: "", status: "idle" }]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => {
      const row = prev.find((r) => r.id === id);
      if (row?.audioUrl) URL.revokeObjectURL(row.audioUrl);
      return prev.filter((r) => r.id !== id);
    });
  };

  const updateRow = (id: string, updates: Partial<ProviderRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  };

  const handleGenerate = async (rowId: string) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row || !row.voiceId.trim() || !previewText.trim()) return;

    // Clean up previous audio
    if (row.audioUrl) URL.revokeObjectURL(row.audioUrl);
    updateRow(rowId, { status: "generating", audioUrl: undefined, audioBlob: undefined, ttfaMs: undefined, error: undefined });

    const startTime = performance.now();
    try {
      const response = await makeRequest<Blob>("/v1/tts/provider", {
        method: "POST",
        body: { text: previewText, provider: row.provider, voiceId: row.voiceId.trim() },
        blob: true,
      });
      const ttfaMs = Math.round(performance.now() - startTime);
      if (response.size === 0) throw new Error("Empty audio response");
      const audioUrl = URL.createObjectURL(response);
      updateRow(rowId, { status: "done", audioUrl, audioBlob: response, ttfaMs });
    } catch (err: any) {
      const ttfaMs = Math.round(performance.now() - startTime);
      const raw = err?.message || "Generation failed";
      const providerLabel = PROVIDER_CONFIG[row.provider]?.label || row.provider;
      let error = raw;
      if (/rate limit/i.test(raw)) {
        error = raw;
      } else if (/404|not found|invalid.*voice|unknown.*voice|does not exist/i.test(raw)) {
        error = `Voice ID "${row.voiceId}" was not found on ${providerLabel}. Check the ID and try again, or browse their voice library for valid IDs.`;
      } else if (/401|403|unauthorized|forbidden|auth/i.test(raw)) {
        error = `Authentication failed for ${providerLabel}. The API key may be missing or invalid.`;
      }
      updateRow(rowId, { status: "error", ttfaMs, error });
    }
  };

  const handleGenerateAll = async () => {
    const validRows = rows.filter((r) => r.voiceId.trim() && previewText.trim());
    if (validRows.length === 0) return;
    await Promise.allSettled(validRows.map((r) => handleGenerate(r.id)));
  };

  const handleDownload = (row: ProviderRow) => {
    if (!row.audioUrl || !row.audioBlob) return;
    const a = document.createElement("a");
    a.href = row.audioUrl;
    a.download = `${row.provider}_${row.voiceId}.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSelectRecommended = (provider: string, voiceId: string, displayName?: string) => {
    const emptyRow = rows.find((r) => !r.voiceId.trim() && r.status === "idle");
    if (emptyRow) {
      updateRow(emptyRow.id, { provider, voiceId, displayName });
    } else {
      const newId = nextRowId();
      setRows((prev) => [...prev, { id: newId, provider, voiceId, displayName, status: "idle" }]);
    }
  };

  const validCount = rows.filter((r) => r.voiceId.trim()).length;
  const anyGenerating = rows.some((r) => r.status === "generating");

  const ttfaColor = (ms: number) => (ms < 500 ? "text-emerald-400" : ms < 1500 ? "text-amber-400" : "text-red-400");

  const ttfaBg = (ms: number) =>
    ms < 500 ? "bg-emerald-500/10 border-emerald-500/20" : ms < 1500 ? "bg-amber-500/10 border-amber-500/20" : "bg-red-500/10 border-red-500/20";

  return (
    <View className="flex flex-col gap-6 mt-6">
      {/* Prompt input */}
      <View className="flex flex-col gap-1.5">
        <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Input Text</Text>
        <div className="relative">
          <textarea
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            maxLength={200}
            className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/60 text-sm text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 dark:focus:border-blue-500 transition-colors leading-relaxed placeholder:text-gray-400 dark:placeholder:text-gray-600"
            rows={2}
            placeholder="Type or paste the text you want synthesized..."
          />
          <div className="absolute bottom-3 right-4 text-[10px] font-mono text-gray-300 dark:text-gray-600 pointer-events-none">{previewText.length}/200</div>
        </div>
      </View>

      {/* Recommended Voices */}
      <View className="flex flex-col">
        <button
          onClick={() => setIsRecommendedOpen((prev) => !prev)}
          className="flex flex-row items-center gap-2 py-1 group/rec cursor-pointer"
        >
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Recommended Voices</Text>
          <Icon
            name={isRecommendedOpen ? "chevron-up" : "chevron-down"}
            size={16}
            className="text-gray-400 dark:text-gray-500 group-hover/rec:text-gray-600 dark:group-hover/rec:text-gray-300 transition-colors"
          />
        </button>
        {isRecommendedOpen && (
          <View className="flex flex-col gap-3 mt-3">
            {/* Use case tabs */}
            <View className="flex flex-row gap-1 overflow-x-auto pb-0.5 scrollbar-hide">
              {RECOMMENDED_VOICES.map((useCase, idx) => (
                <button
                  key={useCase.label}
                  onClick={() => setSelectedUseCase(idx)}
                  className={`flex flex-row items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer shrink-0 ${
                    idx === selectedUseCase
                      ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                      : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent"
                  }`}
                >
                  <Icon
                    name={useCase.icon}
                    size={14}
                    className={`shrink-0 ${idx === selectedUseCase ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}`}
                  />
                  {useCase.label}
                </button>
              ))}
            </View>
            {/* Voice pills for selected use case */}
            <View className="flex flex-row flex-wrap gap-1.5">
              {RECOMMENDED_VOICES[selectedUseCase].voices.map((voice) => (
                <Tooltip
                  key={`${voice.provider}-${voice.voiceId}`}
                  content={voice.description}
                >
                  <button
                    onClick={() => handleSelectRecommended(voice.provider, voice.voiceId, voice.name)}
                    className="flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium leading-none bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-600 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors cursor-pointer"
                  >
                    <div
                      className={`flex items-center justify-center h-4 shrink-0 ${voice.provider === "azure" || voice.provider === "elevenlabs" ? "-translate-y-px" : ""}`}
                    >
                      <ProviderLogo
                        provider={voice.provider}
                        size="sm"
                        className="max-w-[70px]"
                      />
                    </div>
                    {voice.name}
                  </button>
                </Tooltip>
              ))}
            </View>
          </View>
        )}
      </View>

      {/* Voices header */}
      <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Voices</Text>

      {/* Voice rows */}
      <View className="flex flex-col gap-3">
        {rows.map((row) => {
          const config = PROVIDER_CONFIG[row.provider];
          const canGenerateRow = row.voiceId.trim() && previewText.trim() && row.status !== "generating";
          return (
            <View
              key={row.id}
              className="group rounded-lg border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/30 p-4"
            >
              {/* Provider header */}
              <View className="flex flex-row items-center gap-3 mb-3">
                <ProviderLogo
                  provider={row.provider}
                  size="md"
                />
                <View className="w-[170px] shrink-0">
                  <DropdownField
                    value={row.provider}
                    onChange={(value) =>
                      updateRow(row.id, {
                        provider: value,
                        voiceId: "",
                        status: "idle",
                        audioUrl: undefined,
                        audioBlob: undefined,
                        ttfaMs: undefined,
                        error: undefined,
                      })
                    }
                    options={PROVIDER_OPTIONS}
                  />
                </View>
                <div className="flex-1" />
                <button
                  onClick={() => removeRow(row.id)}
                  className="flex items-center justify-center w-7 h-7 rounded-md text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Icon
                    name="close"
                    size={14}
                  />
                </button>
              </View>

              {/* Voice ID + Generate */}
              <View className="flex flex-row items-center gap-2">
                <View className="flex-1 relative">
                  <InputField
                    placeholder={config?.idPlaceholder || "Voice ID"}
                    value={row.voiceId}
                    onChange={(value) => updateRow(row.id, { voiceId: value, displayName: undefined })}
                    className="w-full font-mono"
                  />
                  {row.displayName && row.displayName !== row.voiceId && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 dark:text-gray-500 pointer-events-none">
                      ({row.displayName})
                    </span>
                  )}
                </View>
                <Button
                  variant="info"
                  size="sm"
                  outline
                  onClick={() => handleGenerate(row.id)}
                  disabled={!canGenerateRow}
                >
                  {row.status === "generating" ? (
                    <>
                      <Icon
                        name="loader"
                        size={14}
                        className="animate-spin"
                      />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Icon
                        name="play"
                        size={14}
                      />
                      Generate
                    </>
                  )}
                </Button>
              </View>

              {config && (
                <View className="flex flex-row items-center gap-3 mt-2 pl-0.5">
                  <button
                    onClick={() => updateRow(row.id, { voiceId: config.exampleVoiceId })}
                    className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                    title={`Use example: ${config.exampleVoiceName}`}
                  >
                    Try <span className="font-mono">{config.exampleVoiceName}</span>
                  </button>
                  <span className="text-gray-300 dark:text-gray-700">|</span>
                  <a
                    href={config.voiceLibraryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Browse voices <span className="inline-block translate-y-[-1px]">&#8599;</span>
                  </a>
                </View>
              )}

              {/* Error result */}
              {row.status === "error" && (
                <Alert
                  variant="destructive"
                  className="mt-3"
                >
                  <AlertDescription className="flex items-center gap-2">
                    <Icon
                      name="error"
                      size={16}
                      className="shrink-0"
                    />
                    <span className="flex-1">{row.error}</span>
                    {row.ttfaMs !== undefined && <span className="text-[10px] font-mono opacity-60">{(row.ttfaMs / 1000).toFixed(2)}s</span>}
                  </AlertDescription>
                </Alert>
              )}

              {/* Audio result */}
              {row.status === "done" && row.audioUrl && (
                <View className="flex flex-row items-center gap-3 mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/30">
                  <audio
                    controls
                    src={row.audioUrl}
                    className="flex-1 h-8"
                  >
                    Your browser does not support the audio element.
                  </audio>
                  {row.ttfaMs !== undefined && (
                    <Tooltip content="Time to first audio">
                      <View
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono tabular-nums shrink-0 ${ttfaBg(row.ttfaMs)} ${ttfaColor(row.ttfaMs)}`}
                      >
                        <Icon
                          name="speed"
                          size={11}
                        />
                        {(row.ttfaMs / 1000).toFixed(2)}s
                      </View>
                    </Tooltip>
                  )}
                  <Tooltip content="Download WAV">
                    <button
                      onClick={() => handleDownload(row)}
                      className="flex items-center justify-center w-8 h-8 rounded-md text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
                    >
                      <Icon
                        name="download"
                        size={16}
                      />
                    </button>
                  </Tooltip>
                </View>
              )}
            </View>
          );
        })}

        {/* Placeholder for adding next voice */}
        <button
          onClick={addRow}
          className="group/add rounded-lg border border-dashed border-gray-300 dark:border-gray-700/50 hover:border-gray-400 dark:hover:border-gray-600 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors py-5 flex items-center justify-center gap-2"
        >
          <Icon
            name="add"
            size={14}
            className="text-gray-400 dark:text-gray-600 group-hover/add:text-gray-500 dark:group-hover/add:text-gray-400 transition-colors"
          />
          <span className="text-xs font-medium text-gray-400 dark:text-gray-600 group-hover/add:text-gray-500 dark:group-hover/add:text-gray-400 transition-colors">
            Add another voice
          </span>
        </button>
      </View>

      {/* Generate All */}
      {validCount > 1 && (
        <Button
          variant="info"
          size="sm"
          outline
          onClick={handleGenerateAll}
          disabled={anyGenerating || validCount === 0 || !previewText.trim()}
          className="justify-center w-full"
        >
          <Icon
            name="play"
            size={14}
          />
          {anyGenerating ? "Generating..." : `Generate All ${validCount} Voices`}
        </Button>
      )}
    </View>
  );
};

// ─── Provider Latency ───────────────────────────────────────────────────────────
// Mirrors the LLM Lab aggregates table for TTS: p50/p95 time-to-first-audio
// and full synthesis duration per provider+model, sourced from the agents'
// production histograms via /v1/tts-lab/aggregates. Global across all
// tenants — the source metrics carry no org/agent labels.

type LatencySortKey = "model" | "ttfaP50" | "ttfaP95" | "durationP50" | "durationP95" | "sampleCount";

const LATENCY_PROVIDER_OPTIONS: { label: string; value: string }[] = [
  { label: "All providers", value: "" },
  ...Object.entries(PROVIDER_CONFIG).map(([key, config]) => ({ label: config.label, value: key })),
];

const ProviderLatencyTab = () => {
  const dispatch = useAppDispatch();
  const { aggregates, loading, error } = useAppSelector((state) => state.ttsLab);

  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const [provider, setProvider] = useState<string>("");
  const [sort, setSort] = useState<SortState<LatencySortKey>>({ key: "sampleCount", dir: "desc" });

  const handleSort = (key: LatencySortKey) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { key, dir: key === "model" ? "asc" : "desc" };
    });
  };

  const fetchParams = useMemo(() => {
    const { startDate, endDate } = getDateRange(timeRange);
    return { startDate, endDate, ...(provider ? { provider } : {}) };
  }, [timeRange, provider]);

  useEffect(() => {
    dispatch(getTtsAggregates(fetchParams));
    return () => {
      dispatch(clearTtsLab());
    };
  }, [dispatch, fetchParams]);

  const sortedAggregates = useMemo(() => {
    const arr = [...aggregates];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sort.dir === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return arr;
  }, [aggregates, sort]);

  const totalSamples = aggregates.reduce((sum, agg) => sum + agg.sampleCount, 0);
  const gridCols = "grid-cols-[1.6fr_repeat(4,minmax(0,1fr))_minmax(0,0.9fr)]";

  return (
    <View className="flex flex-col gap-4 mt-6">
      <Card className="p-4">
        <View className="flex flex-col gap-4">
          <View className="flex flex-row flex-wrap gap-x-8 gap-y-4">
            <FilterSection label="Provider">
              <View className="w-44">
                <DropdownField
                  value={provider}
                  onChange={(v) => setProvider(v ?? "")}
                  options={LATENCY_PROVIDER_OPTIONS}
                />
              </View>
            </FilterSection>
            <FilterSection label="Time range">
              <PillGroup
                options={TIME_RANGE_OPTIONS}
                value={timeRange}
                onChange={setTimeRange}
              />
            </FilterSection>
          </View>
          {!loading && !error && (
            <Text className="text-xs text-gray-400 dark:text-gray-500">
              {aggregates.length} row{aggregates.length === 1 ? "" : "s"} · {formatCount(totalSamples)} synthesis
              {totalSamples === 1 ? "" : " samples"} · global across all agents
            </Text>
          )}
        </View>
      </Card>

      {loading && (
        <View className="flex items-center justify-center h-64">
          <Spinner size={24} />
        </View>
      )}

      {!loading && error && (
        <View className="flex flex-col items-center justify-center h-64 gap-3">
          <Icon
            name="error"
            size={32}
            className="text-red-400"
          />
          <Text className="text-sm text-red-500">{error}</Text>
          <Button
            variant="neutral"
            size="auto"
            onClick={() => dispatch(getTtsAggregates(fetchParams))}
          >
            Retry
          </Button>
        </View>
      )}

      {!loading && !error && aggregates.length === 0 && (
        <EmptyState
          icon="bar-chart-2"
          title="No TTS data for this slice"
          description="Try a wider time range or a different provider. Data is captured as agents synthesize speech in production."
        />
      )}

      {!loading && !error && aggregates.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <View className={`grid ${gridCols} gap-0 text-[10.5px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 px-4 py-3 border-b border-gray-100 dark:border-gray-800`}>
            <SortHeader
              label="Provider / Model"
              sortKey="model"
              sort={sort}
              onSort={handleSort}
              align="left"
            />
            <SortHeader
              label="TTFA p50"
              sortKey="ttfaP50"
              sort={sort}
              onSort={handleSort}
            />
            <SortHeader
              label="TTFA p95"
              sortKey="ttfaP95"
              sort={sort}
              onSort={handleSort}
            />
            <SortHeader
              label="Duration p50"
              sortKey="durationP50"
              sort={sort}
              onSort={handleSort}
            />
            <SortHeader
              label="Duration p95"
              sortKey="durationP95"
              sort={sort}
              onSort={handleSort}
            />
            <SortHeader
              label="Samples"
              sortKey="sampleCount"
              sort={sort}
              onSort={handleSort}
            />
          </View>
          {sortedAggregates.map((agg) => (
            <View
              key={`${agg.provider}|${agg.model}`}
              className={`grid ${gridCols} gap-0 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0 items-center`}
            >
              <View className="flex flex-col min-w-0">
                <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {PROVIDER_CONFIG[agg.provider]?.label ?? agg.provider}
                </Text>
                <Text className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{agg.model || "—"}</Text>
              </View>
              <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.ttfaP50)}</Text>
              <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.ttfaP95)}</Text>
              <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.durationP50)}</Text>
              <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.durationP95)}</Text>
              <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatCount(agg.sampleCount)}</Text>
            </View>
          ))}
        </Card>
      )}
    </View>
  );
};
